import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApplicationStatus } from 'enums/ApplicationStatus';
import { ApplicationType } from 'enums/ApplicationType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { LoanPurpose } from 'enums/LoanPurpose';
import { SwimmingPoolType } from 'enums/SwimmingPoolType';
import { ApplicationVariablesDto, DashboardAdditionalInfo } from 'api/ApplicationApi';

export interface EmployeeDisplayProps {
  name: string;
  position: string;
  email: string;
  phone?: string;
}

export interface ApplicationState {
  [LoanInformationVariables.LoanAmount]?: number;
  [LoanInformationVariables.LoanType]?: ApplicationType;
  [LoanInformationVariables.LoanPurpose]?: LoanPurpose;
  [LoanInformationVariables.OtherHomeImprovementType]?: string;
  [LoanInformationVariables.SessionId]?: string;
  [LoanInformationVariables.IsBorrowerManualFlow]?: boolean;
  [LoanInformationVariables.IsCoBorrowerManualFlow]?: boolean;
  [LoanInformationVariables.ProcessingStatusSetDate]?: Date;
  [LoanInformationVariables.AssignedConsultant]?: EmployeeDisplayProps;
  [LoanInformationVariables.AssignedProcessor]?: EmployeeDisplayProps;
  [LoanInformationVariables.BorrowerChangedInfoAfterProvePreFill]?: boolean;
  [LoanInformationVariables.CoBorrowerChangedInfoAfterProvePreFill]?: boolean;
  [LoanInformationVariables.SwimmingPoolType]?: SwimmingPoolType;
  applicationId?: string | null;
  displayId?: string;
  variables?: ApplicationVariablesDto;
  applicationStatus?: ApplicationStatus;
  createdDate?: Date;
  messageFromHFS?: string;
  productName?: string;
  messageFromHfsShowOnPortal?: boolean | null;
  dashboardAdditionalInfo?: DashboardAdditionalInfo;
}

const initialState: ApplicationState = {
  applicationId: null,
  variables: {},
  dashboardAdditionalInfo: {
    isAdditionalInfoTabRequired: false,
    isBorrowerCurrentEmploymentsRequired: false,
    isPartialBorrowerCurrentEmploymentsRequired: false,
    isBorrowerCurrentEmploymentsCompleted: false,
    isBorrowerPreviousEmploymentRequired: false,
    isBorrowerPreviousEmploymentCompleted: false,
    isBorrowerEmploymentStatusInformationRequired: false,
    isBorrowerEmploymentStatusInformationCompleted: false,
    isCoBorrowerCurrentEmploymentsRequired: false,
    isPartialCoBorrowerCurrentEmploymentsRequired: false,
    isCoBorrowerCurrentEmploymentsCompleted: false,
    isCoBorrowerPreviousEmploymentRequired: false,
    isCoBorrowerPreviousEmploymentCompleted: false,
    isCoBorrowerEmploymentStatusInformationRequired: false,
    isCoBorrowerEmploymentStatusInformationCompleted: false,
    isContractorEmploymentRequired: false,
    isContractorEmploymentCompleted: false,
  },
};

const application = createSlice({
  name: 'application',
  initialState: initialState as ApplicationState,
  reducers: {
    setApplication: (state, { payload }: PayloadAction<ApplicationState>) => {
      state = { ...state, ...payload };

      return state;
    },

    setApplicationType: (state, { payload }: PayloadAction<ApplicationType | undefined>) => {
      state![LoanInformationVariables.LoanType] = payload;

      return state;
    },

    setSessionId: (state, { payload }: PayloadAction<string | undefined>) => {
      state![LoanInformationVariables.SessionId] = payload;

      return state;
    },
    setIsBorrowerManualFlow: (state, { payload }: PayloadAction<boolean>) => {
      state![LoanInformationVariables.IsBorrowerManualFlow] = payload;

      return state;
    },
    setIsCoBorrowerManualFlow: (state, { payload }: PayloadAction<boolean>) => {
      state![LoanInformationVariables.IsCoBorrowerManualFlow] = payload;

      return state;
    },
    setBorrowerChangedInfoAfterProvePreFill: (state, { payload }: PayloadAction<boolean>) => {
      state![LoanInformationVariables.BorrowerChangedInfoAfterProvePreFill] = payload;

      return state;
    },
    setCoBorrowerChangedInfoAfterProvePreFill: (state, { payload }: PayloadAction<boolean>) => {
      state![LoanInformationVariables.CoBorrowerChangedInfoAfterProvePreFill] = payload;

      return state;
    },
    clearApplication: () => initialState,
  },
});

export const {
  setApplication,
  setApplicationType,
  setIsBorrowerManualFlow,
  setIsCoBorrowerManualFlow,
  setSessionId,
  setBorrowerChangedInfoAfterProvePreFill,
  setCoBorrowerChangedInfoAfterProvePreFill,
  clearApplication,
} = application.actions;

export default application.reducer;
